import { Button } from '@material-ui/core';
import React, { useRef, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import { Typography, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Box, Chip, Input, Grid } from '@material-ui/core';
import useStyles from "./styles";
import PageTitle from '../PageTitle/PageTitle';
import { Card, CardContent } from '@material-ui/core';
import { BatteryFullRounded } from '@material-ui/icons';

// -- Docs ---
//
// Expected columns: "ADMARKUP" (required), "SUM(TOTAL_PAID_PRICE_TRUE_COUNT)" (optional/recommended).
// Ad preview logic same as before.
// Only plain CSV (no quoted multiline fields, no escaping inside fields).
//

const HTML_COLUMN = "ADMARKUP";
const TOTAL_COUNT_COLUMN = "SUM(TOTAL_PAID_PRICE_TRUE_COUNT)";
const PAGE_SIZE = 10;

function decodeAdMarkup(encoded) {
  if (!encoded) return "";
  try {
    return decodeURIComponent(encoded.replace(/\+/g, " "));
  } catch {
    return encoded;
  }
}

function escapeHtml(text) {
  if (typeof text !== "string") text = String(text);
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function isVast(str) {
  if (!str) return false;
  const txt = str.toLowerCase();
  return txt.includes("<vast") && txt.includes("<mediafile");
}

function getAllMediaFiles(vastXml) {
  let urls = [];
  try {
    const parser = new window.DOMParser();
    const xml = parser.parseFromString(vastXml, "text/xml");
    const files = xml.getElementsByTagName("MediaFile");
    for (let i = 0; i < files.length; i++) {
      let txt = files[i].textContent.trim();
      if (/^\s*<!\[CDATA\[.*\]\]>\s*$/.test(txt)) {
        txt = txt.replace(/^\s*<!\[CDATA\[(.*?)\]\]>\s*$/s, "$1").trim();
      }
      const type = files[i].getAttribute("type") || "";
      urls.push({ url: txt, type });
    }
    const adtagUris = xml.getElementsByTagName("AdTagURI");
    for (let i = 0; i < adtagUris.length; i++) {
      let uri = adtagUris[i].textContent.trim();
      if (/^\s*<!\[CDATA\[.*\]\]>\s*$/.test(uri)) {
        uri = uri.replace(/^\s*<!\[CDATA\[(.*?)\]\]>\s*$/s, "$1").trim();
      }
      urls.push({ url: uri, type: "adtaguri" });
    }
  } catch {}
  return urls;
}

function isMediaUrl(str) {
  if (!str || typeof str !== "string") return false;
  return /\.(mp4|webm|mov|ogg|mp3|aac)(\?|$)/i.test(str);
}

function isHttpUrl(str) {
  return /^https?:\/\/\S+/i.test(str);
}

function selectPreferredMedia(files) {
  if (!files.length) return null;
  return (
    files.find(f => (f.type || "").match(/mp4|webm|mov|ogg/i)) ||
    files.find(f => f.type === "adtaguri") ||
    files[0]
  );
}

function getNativeObject(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (payload.native) {
    if (typeof payload.native === "string") {
      try {
        return JSON.parse(payload.native);
      } catch {
        return null;
      }
    }
    return payload.native;
  }
  if (payload.assets) return payload;
  return null;
}

function extractNativeAssets(nativeObj) {
  const assets = Array.isArray(nativeObj.assets) ? nativeObj.assets : [];
  let title = "";
  let subtitle = "";
  let imageUrl = "";
  let imageW = 0;
  let imageH = 0;
  let clickUrl = (nativeObj.link && nativeObj.link.url) || "";

  assets.forEach((asset) => {
    if (asset && asset.title && !title) {
      title = asset.title.text || "";
    }
    if (asset && asset.data && !subtitle) {
      subtitle = asset.data.value || "";
    }
    if (asset && asset.img && !imageUrl) {
      imageUrl = asset.img.url || "";
      imageW = asset.img.w || 0;
      imageH = asset.img.h || 0;
    }
    if (!clickUrl && asset && asset.link && asset.link.url) {
      clickUrl = asset.link.url;
    }
  });

  return { title, subtitle, imageUrl, imageW, imageH, clickUrl };
}

function fireImpressionTrackers(urls) {
  if (!Array.isArray(urls)) return;
  urls.forEach((url) => {
    if (isHttpUrl(url)) {
      const img = new Image();
      img.src = url;
    }
  });
}

function renderAdPreview(admarkup) {
  const decoded = decodeAdMarkup(admarkup || "").trim();

  if (isVast(decoded)) {
    const files = getAllMediaFiles(decoded);
    if (files.length) {
      const preferred = selectPreferredMedia(files);
      if (isMediaUrl(preferred.url)) {
        return (
          <Box>
            <video controls width={320} height={250} style={{ background: "#000", objectFit: "contain" }}>
              <source src={preferred.url} type={preferred.type || "video/mp4"} />
              Your browser does not support video.
            </video>
            <Typography variant="caption" color="textSecondary">
              (VAST: {preferred.type})
            </Typography>
          </Box>
        );
      }
      if (preferred.type === "adtaguri") {
        return (
          <Box>
            <Typography variant="body2">
              <b>AdTagURI:</b>{" "}
              <a href={preferred.url} target="_blank" rel="noopener noreferrer">
                {preferred.url}
              </a>
            </Typography>
            <Typography variant="caption" color="textSecondary">
              (External VAST link – download manually)
            </Typography>
          </Box>
        );
      }
      return (
        <>
          {files.map((f, idx) => (
            <div key={idx}>
              <a href={f.url} target="_blank" rel="noopener noreferrer">
                {f.url}
              </a>{" "}
              ({f.type})
            </div>
          ))}
        </>
      );
    } else {
      return (
        <Typography color="error" fontSize={13}>
          No video/audio file in VAST.
        </Typography>
      );
    }
  }
  if (isMediaUrl(decoded)) {
    const ext = decoded.split(".").pop();
    const isVideo = /mp4|webm|mov|ogg/i.test(ext);
    const isAudio = /mp3|aac/i.test(ext);
    if (isVideo) {
      return (
        <Box>
          <video controls width={320} height={250} style={{ background: "#000", objectFit: "contain" }}>
            <source src={decoded} />
          </video>
          <Typography variant="caption" color="textSecondary">
            (Direct video link)
          </Typography>
        </Box>
      );
    }
    if (isAudio) {
      return (
        <Box>
          <audio controls style={{ width: "90%" }}>
            <source src={decoded} />
          </audio>
          <Typography variant="caption" color="textSecondary">
            (Direct audio link)
          </Typography>
        </Box>
      );
    }
  }
  if (isHttpUrl(decoded)) {
    return (
      <a href={decoded} target="_blank" rel="noopener noreferrer">
        {decoded}
      </a>
    );
  }
  return (
    <iframe
      title="admarkup-preview"
      width={320}
      height={250}
      style={{
        border: "1px solid #aaa",
        marginTop: 5,
        borderRadius: 4,
        background: "#fff",
      }}
      sandbox="allow-scripts allow-same-origin"
      srcDoc={decoded}
    />
  );
}

function containsVodka(row) {
  const val = (row[HTML_COLUMN] || "").toLowerCase();
  return val.includes("wódka");
}

// --- SIMPLE CSV PARSER ---
// Accepts only plain CSV: no quoted commas/newlines. Autodetects "," or ";".
function parseCsv(text) {
  // Pick separator: if ";" found in first line, use ";"
  const firstLine = text.split(/\r?\n/, 1)[0];
  const sep = firstLine.includes(";") ? ";" : ",";
  const lines = text.split(/\r?\n/).filter(line => line.trim().length);
  if (!lines.length) return [];
  const headers = lines[0].split(sep).map(h => h.trim());
  return lines.slice(1).map(line => {
    const values = line.split(sep);
    let row = {};
    headers.forEach((h, idx) => {
      row[h] = values[idx] ?? "";
    });
    return row;
  });
}

// -- Main component --
function CsvAdTable() {
  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [page, setPage] = useState(0);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const text = evt.target.result;
        const data = parseCsv(text);
        if (!data.length) {
          setTableData([]);
          setColumns([]);
          setError("No data to display or invalid CSV.");
          return;
        }
        setColumns(Object.keys(data[0]));
        // Sort logic
        let vodka = [],
          rest = [];
        data.forEach((row) => {
          if (containsVodka(row)) vodka.push(row);
          else rest.push(row);
        });
        rest.sort((a, b) => {
          const aVal = parseFloat((a[TOTAL_COUNT_COLUMN] || "0").replace(",", "."));
          const bVal = parseFloat((b[TOTAL_COUNT_COLUMN] || "0").replace(",", "."));
          return bVal - aVal;
        });
        setTableData([...vodka, ...rest]);
        setPage(0);
      } catch (err) {
        setError("Could not parse file. Only simple CSV supported.");
        setTableData([]);
        setColumns([]);
      }
    };
    reader.readAsText(file);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", my: 4 }}>
      <Typography variant="h4" gutterBottom>
        Upload CSV with Ad Creatives
      </Typography>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Button
          variant="contained"
          component="label"

        >
          Choose CSV File
          <Input
            type="file"
            inputProps={{ accept: ".csv" }}
            onChange={handleFileChange}
            sx={{ display: "none" }}
          />
        </Button>
        <Chip
          label={`Expected columns: "${HTML_COLUMN}", "${TOTAL_COUNT_COLUMN}", ...`}
          color="info"
          variant="outlined"
        />
      </Box>
      <Typography variant="caption" color="warning.main">
        Only simple CSV supported – no multiline/quoted fields. Separator can be "," or ";".
      </Typography>
      {error && (
        <Typography color="error" mt={1}>
          {error}
        </Typography>
      )}
      {tableData.length ? (
        <Paper elevation={2} sx={{ mb: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns
                    .filter((col) => col !== HTML_COLUMN)
                    .map((col) => (
                      <TableCell key={col}>{col}</TableCell>
                    ))}
                  <TableCell>Preview</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData
                  .slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
                  .map((row, idx) => (
                    <TableRow
                      key={idx}
                      sx={
                        containsVodka(row)
                          ? { background: "#ffe6e6" }
                          : undefined
                      }
                    >
                      {columns
                        .filter((col) => col !== HTML_COLUMN)
                        .map((col) => (
                          <TableCell
                            key={col}
                            sx={{
                              maxWidth: 400,
                              overflowX: "auto",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-all",
                              fontSize: "0.95em",
                            }}
                          >
                            {row[col] ?? ""}
                          </TableCell>
                        ))}
                      <TableCell>{renderAdPreview(row[HTML_COLUMN])}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={tableData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
        </Paper>
      ) : (
        <Typography color="textSecondary">
          No data to display. Please upload a CSV file.
        </Typography>
      )}
    </Box>
  );
}


export default function AdRenderers() {

 const classes = useStyles();
const ad = useRef();
const adm = useRef();
const adm2 = useRef();
const adm3 = useRef();
const [timeMinutes, settimeMinutes] = useState("");
const [vastadm, setvastadm] = useState("");
const [vastError, setvastError] = useState("");
const [nativeData, setNativeData] = useState(null);
const [nativeError, setNativeError] = useState("");
const [nativeLoading, setNativeLoading] = useState(false);

React.useEffect(() => {
  const updateTime = () => {
    const date = new Date();
    let h = date.getHours();
    let m = date.getMinutes();
    h = (h < 10) ? "0" + h : h;
    m = (m < 10) ? "0" + m : m;
    settimeMinutes(`${h}:${m}`);
  };
  updateTime();
  const timer = setInterval(updateTime, 1000);
  return () => clearInterval(timer);
}, []);

  function Render()
  {
    var render = adm.current.value;
    var iframka = ad.current;
    iframka.srcdoc = render;
    


  }
  async function Render3() {
    setvastadm("");
    setvastError("");
    const xmlString = (adm3.current.value || "").trim();
    if (!xmlString) {
      setvastError("Please paste VAST XML.");
      return;
    }
    try {
      let files = getAllMediaFiles(xmlString);
      let preferred = selectPreferredMedia(files);
      if (preferred && preferred.type === "adtaguri" && isHttpUrl(preferred.url)) {
        const resp = await fetch(preferred.url);
        const text = await resp.text();
        files = getAllMediaFiles(text);
        preferred = selectPreferredMedia(files);
      }
      if (preferred && isMediaUrl(preferred.url)) {
        setvastadm(preferred.url);
        return;
      }
      setvastError("No playable media file found in VAST.");
    } catch (err) {
      setvastError("Failed to parse VAST. Please check the XML.");
    }
  }

  async function Render2() {
    setNativeError("");
    setNativeData(null);
    setNativeLoading(true);
    const raw = (adm2.current.value || "").trim();
    if (!raw) {
      setNativeError("Please paste native JSON.");
      setNativeLoading(false);
      return;
    }
    try {
      let payload = JSON.parse(raw);
      let assetUrl = null;
      if (payload && typeof payload === "object") {
        assetUrl = payload.assetsurl || payload.assetsUrl || payload.assets_url || null;
      }
      if (assetUrl && isHttpUrl(assetUrl)) {
        const resp = await fetch(assetUrl);
        payload = await resp.json();
      }
      const nativeObj = getNativeObject(payload);
      if (!nativeObj) {
        setNativeError("No native object/assets found in JSON.");
        setNativeLoading(false);
        return;
      }
      const data = extractNativeAssets(nativeObj);
      if (!data.title && !data.imageUrl) {
        setNativeError("Native assets are missing title/image.");
        setNativeLoading(false);
        return;
      }
      setNativeData(data);
      fireImpressionTrackers(nativeObj.imptrackers);
    } catch (err) {
      setNativeError("Invalid JSON or failed to load assets.");
    } finally {
      setNativeLoading(false);
    }
  }

return (
    <Box className={classes.pageRoot}>
      <PageTitle title="Ad Renderers" />

      <Card className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            HTML Ad Renderer
          </Typography>
          <Typography variant="body2" color="textSecondary" className={classes.sectionSubtitle}>
            Paste HTML ad markup to render in a sandboxed iframe.
          </Typography>
          <Grid container spacing={3} className={classes.sectionGrid}>
            <Grid item xs={12} md={6}>
              <TextField
                variant="outlined"
                placeholder="Insert simple HTML ad markup"
                inputRef={adm}
                rows={10}
                fullWidth
                multiline
                id="tocheck"
                style={{ borderRadius: "10px", textAlign: "center", width: "100%", height: "200px", resize: "vertical" }}
                className="inline-txtarea"
              />
              <Box className={classes.formActions}>
                <br></br>
                <Button variant="outlined" color="primary" onClick={Render}>
                  Render
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.previewPanel}>
                <div className={classes.smartphone}>
                  <div className={classes.nav}>
                    {timeMinutes}
                    <BatteryFullRounded className={classes.battery} />
                  </div>
                  <iframe
                    ref={ad}
                    sandbox="allow-forms allow-pointer-lock allow-popups allow-same-origin allow-scripts allow-top-navigation"
                    className={classes.adFrame}
                    title="ad-preview"
                  />
                </div>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            Native Ad Renderer
          </Typography>
          <Typography variant="body2" color="textSecondary" className={classes.sectionSubtitle}>
            Paste OpenRTB native JSON or a payload with a native object.
          </Typography>
          <Grid container spacing={3} className={classes.sectionGrid}>
            <Grid item xs={12} md={6}>
              <TextField
                variant="outlined"
                placeholder="Insert native ad markup"
                inputRef={adm2}
                rows={10}
                fullWidth
                multiline
                id="tocheck"
                style={{ borderRadius: "10px", textAlign: "center", width: "100%", height: "200px", resize: "vertical" }}
                className="inline-txtarea"
              />
              <Box className={classes.formActions}>
                <br></br>
                <Button variant="outlined" color="primary" onClick={Render2}>
                  Render
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.previewPanel}>
                <Box className={classes.content2}>
                  {nativeLoading && (
                    <Typography variant="body2">Loading native assets...</Typography>
                  )}
                  {nativeError && (
                    <Typography color="error">{nativeError}</Typography>
                  )}
                  {nativeData && (
                    <a href={nativeData.clickUrl || "#"} target="_blank" rel="noopener noreferrer">
                      {nativeData.imageUrl && (
                        <img
                          src={nativeData.imageUrl}
                          alt={nativeData.title || "native ad"}
                          style={{
                            width: nativeData.imageW ? `${nativeData.imageW}px` : "auto",
                            height: nativeData.imageH ? `${nativeData.imageH}px` : "auto",
                            maxWidth: "100%",
                          }}
                        />
                      )}
                      <h3>{nativeData.title}</h3>
                      {nativeData.subtitle && (
                        <Typography variant="body2" color="textSecondary">
                          {nativeData.subtitle}
                        </Typography>
                      )}
                    </a>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            VAST Renderer
          </Typography>
          <Typography variant="body2" color="textSecondary" className={classes.sectionSubtitle}>
            Paste VAST XML; a direct media file will be selected if available.
          </Typography>
          <Grid container spacing={3} className={classes.sectionGrid}>
            <Grid item xs={12} md={6}>
              <TextField
                variant="outlined"
                placeholder="Insert VAST markup"
                inputRef={adm3}
                rows={10}
                fullWidth
                multiline
                id="tocheck"
                style={{ borderRadius: "10px", textAlign: "center", width: "100%", height: "200px", resize: "vertical" }}
                className="inline-txtarea"
              />
              <Box className={classes.formActions}>
                <br></br>
                <Button variant="outlined" color="primary" onClick={Render3}>
                  Render
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.previewPanel}>
                {vastError && (
                  <Typography color="error">{vastError}</Typography>
                )}
                {vastadm && (
                  <video
                    controls
                    autoPlay
                    style={{ height: 360, width: "100%", maxWidth: 520, background: "#000" }}
                  >
                    <source src={vastadm} />
                    Your browser does not support video.
                  </video>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            Bulk Renderer
          </Typography>
          <Typography variant="body2" color="textSecondary" className={classes.sectionSubtitle}>
            Upload CSV with ad creatives to preview rows and media.
          </Typography>
          <Box style={{ marginTop: 16 }}>
            <CsvAdTable />
          </Box>
        </CardContent>
      </Card>
    </Box>
);
}
